import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ViewFaultModal from './ViewFaultModal'
import AddEditFaultModal from './AddEditFaultModal'
import BulkUploadFaultsModal from './BulkUploadFaultsModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, InformationIcon, EnabledIcon, DisabledIcon } from '../../../icons'
import { troubleshootingUploadDeletePermission, troubleshootingUploadWritePermission } from '../../../utils/permissionValidation'
import { groupModelsByDevice } from '../../../utils/functions'
import { faultsUploadInstructions } from '../../../utils/fixedUrls'
import {
  loadFaultsUpload,
  loadFaultDetails,
  loadFaultUserGroups,
  removeFault,
  clearTroubleshootingMessages,
  clearFaultDetails,
} from '../../../actions/troubleshooting'
import { loadLanguages, loadModels } from '../../../actions/shared'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'fault',
    order: 'asc',
    translationKey: 'fault_name_asc',
  },
  {
    field: 'fault',
    order: 'desc',
    translationKey: 'fault_name_desc',
  },
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'videos',
    order: 'asc',
    translationKey: 'videos_no_asc',
  },
  {
    field: 'videos',
    order: 'desc',
    translationKey: 'videos_no_desc',
  },
  {
    field: 'isActive',
    order: 'asc',
    translationKey: 'status_asc',
  },
  {
    field: 'isActive',
    order: 'desc',
    translationKey: 'status_desc',
  },
]

const Troubleshooting = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [faultsTableData, setFaultsTableData] = useState([])
  const [statusesFilter, setStatusesFilter] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState(null)
  const [selectedFilterLanguage, setSelectedFilterLanguage] = useState(null)
  const [languages, setLanguages] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedFault, setExpandedFault] = useState(0)
  const [showAddEditFaultModal, setShowAddEditFaultModal] = useState(false)
  const [showViewFaultModal, setShowViewFaultModal] = useState(false)
  const [showDeleteFaultConfirmModal, setShowDeleteFaultConfirmModal] = useState(false)
  const [faultIdToDelete, setFaultIdToDelete] = useState(null)
  const Statuses = ['active', 'inactive']
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)

  useEffect(() => {
    if (props.languages.length === 0) {
      props.actions.loadLanguages()
    }

    if (props.models.length === 0) {
      props.actions.loadModels()
    }

    if (props.faultUserGroups.length === 0) {
      props.actions.loadFaultUserGroups()
    }
  }, [props.actions])

  useEffect(() => {
    const sortedLanguageCodes = props.languages
      .map((lang) => ({
        code: `${lang}-lang`,
        name: t([`filter_context.${lang}-lang`, lang]),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((item) => item.code)

    setLanguages(sortedLanguageCodes)
  }, [props.languages])

  useEffect(() => {
    const getUniqueCFLanguages = (languages) => {
      if (languages.length === 0) {
        return ''
      }

      let uniqueLanguages = new Set()

      languages.forEach((lang) => {
        if (lang.length > 0) {
          uniqueLanguages.add(lang)
        }
      })

      return Array.from(uniqueLanguages)
        .sort((a, b) => a.localeCompare(b))
        .join(', ')
    }

    const tableData = props.faultsUpload.map((fault, index) => {
      return {
        index,
        identifier: fault.id,
        faultNo: fault.fault,
        description: fault.descriptions[0] || '',
        model: groupModelsByDevice(fault.models),
        languages: getUniqueCFLanguages(fault.languageCodes || []),
        videosNo: fault.videos?.length || 0,
        status: fault.isActive ? Statuses[0] : Statuses[1] || '',
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditFault(fault.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditFault(fault.id, true)
            },
            requiredPermission: troubleshootingUploadWritePermission,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(fault.id)
            },
            requiredPermission: troubleshootingUploadDeletePermission,
            isDisabled: fault.isActive,
          },
        ],
      }
    })

    setFaultsTableData(tableData)
  }, [props.faultsUpload])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearTroubleshootingMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearTroubleshootingMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadFaults(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadFaults(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterModel, statusesFilter, selectedFilterLanguage, orderBy])

  const handleLoadFaults = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadFaultsUpload(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterModel(null)
    setSelectedFilterLanguage(null)
    setStatusesFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterModel) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterLanguage) {
      queryParams = { ...queryParams, languageCode: selectedFilterLanguage }
    }

    if (statusesFilter.length === 1) {
      queryParams = { ...queryParams, isActive: statusesFilter[0] === Statuses[0] }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEditFault = (faultId, isEditModal) => {
    if (!faultId) {
      return
    }

    props.actions.loadFaultDetails(faultId).then(() => {
      isEditModal ? setShowAddEditFaultModal(true) : setShowViewFaultModal(true)
    })
  }

  const showConfirmationModal = (id) => {
    setFaultIdToDelete(id)
    setShowDeleteFaultConfirmModal(true)
  }

  const handleDeleteFault = () => {
    setShowDeleteFaultConfirmModal(false)
    props.actions.removeFault(faultIdToDelete)
  }

  const closeModal = () => {
    props.actions.clearFaultDetails()
    setShowAddEditFaultModal(false)
    setShowViewFaultModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  const renderStatus = (status) => {
    switch (status) {
      case Statuses[0]:
        return (
          <div className="status-active">
            <EnabledIcon />
            <p>{t('active')}</p>
          </div>
        )
      case Statuses[1]:
        return (
          <div className="status-inactive">
            <DisabledIcon />
            <p>{t('inactive')}</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterModel || selectedFilterLanguage || statusesFilter.length > 0}
            counterBadge={(selectedFilterModel && 1) + (selectedFilterLanguage && 1) + statusesFilter.length}
            minSearchLength={2}
          />
          <SimpleButton className="submit-button" onClick={() => setShowAddEditFaultModal(true)} requiredPermission={troubleshootingUploadWritePermission}>
            {t('troubleshoot.add_fault')}
          </SimpleButton>
          <SimpleButton className="submit-button" onClick={() => setShowBulkUploadModal(true)} requiredPermission={troubleshootingUploadWritePermission}>
            {t('troubleshoot.bulk_upload_faults')}
          </SimpleButton>
        </div>
        <div className="information-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="information-wrapper">
            <div className="title">
              {`${t('troubleshooting_context.upload_instructions')} `}
              <span>
                <a href={faultsUploadInstructions} target="_blank" rel="noreferrer" className="external-link">
                  {t('document_context.click_here')}
                </a>
              </span>
            </div>
          </div>
        </div>
        {isMobile && faultsTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && faultsTableData.length > 0 && (
          <GenericTable
            data={faultsTableData}
            headers={[
              {
                title: t('troubleshoot.fault_name'),
                orderKey: 'fault',
              },
              {
                title: t('description'),
              },
              {
                title: t('model'),
                orderKey: 'model',
              },
              {
                title: t('languages'),
              },
              {
                title: t('nav.videos'),
                orderKey: 'videos',
              },
              {
                title: t('status'),
                orderKey: 'isActive',
              },
              {
                title: '',
              },
            ]}
            keys={['faultNo', 'description', 'model', 'languages', 'videosNo', 'status', 'action']}
            keyRenderer={{
              status: (item) => {
                return <div className="status-wrapper">{renderStatus(item.status)}</div>
              },
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(fault) => handleOpenViewEditFault(fault.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          faultsTableData.map((fault, index) => (
            <div key={index} className="card" onClick={() => setExpandedFault(index)}>
              <div className={'card-item' + (expandedFault !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('troubleshoot.fault_name')}</div>
                <div className={'card-item-body' + (expandedFault !== index ? ' align-center' : '')}>
                  <div>{fault.faultNo}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={fault.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedFault === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('description')}</div>
                    <div className="card-item-body">{fault.description}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('model')}</div>
                    <div className="card-item-body">{fault.model}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('languages')}</div>
                    <div className="card-item-body">{fault.languages}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('nav.videos')}</div>
                    <div className="card-item-body">{fault.videosNo}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('status')}</div>
                    <div className="card-item-body card-item-body-status">{renderStatus(fault.status)}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && faultsTableData.length === 0 && (
          <EmptyPageResults title={t('troubleshoot.no_faults_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.singleSelect,
              dataset: props.models,
              input: selectedFilterModel,
              output: (filterModel) => {
                setSelectedFilterModel(filterModel)
              },
            },
            {
              name: t('language'),
              type: FilterType.singleSelect,
              dataset: languages,
              input: selectedFilterLanguage ? `${selectedFilterLanguage}-lang` : null,
              output: (filterLanguage) => {
                const language = filterLanguage?.split('-')[0] || null
                setSelectedFilterLanguage(language)
              },
            },
            {
              name: t('status'),
              type: FilterType.multiSelect,
              dataset: Statuses,
              input: statusesFilter,
              output: (filterStatuses) => {
                setStatusesFilter(filterStatuses)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditFaultModal && <AddEditFaultModal closeModal={closeModal} showSpinner={showSpinner} setShowSpinner={setShowSpinner} />}
      {showViewFaultModal && (
        <ViewFaultModal
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditFault={() => {
            setShowViewFaultModal(false)
            setShowAddEditFaultModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showDeleteFaultConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteFaultConfirmModal(false)}
          onAccept={() => handleDeleteFault()}
          onCancel={() => setShowDeleteFaultConfirmModal(false)}
          message={t('troubleshoot.confirm_delete_fault')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={troubleshootingUploadDeletePermission}
        />
      )}
      {showBulkUploadModal && <BulkUploadFaultsModal closeModal={() => setShowBulkUploadModal(false)} />}
    </Fragment>
  )
}

function stateToProps({ shared, troubleshooting }) {
  return {
    faultsUpload: troubleshooting?.faultsUpload || [],
    faultUserGroups: troubleshooting?.faultUserGroups || [],
    languages: shared?.languages?.map((l) => l.languageCode) || [],
    models: [...new Set(shared?.models?.filter((m) => m.ocp_access)?.map((model) => model.model) || [])],
    isLoading: troubleshooting?.isLoading,
    canLoadMore: troubleshooting?.canLoadMoreFaultsUpload,
    successMessage: troubleshooting?.successMessage,
    errorMessage: troubleshooting?.errorMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadFaultsUpload,
        loadFaultUserGroups,
        loadFaultDetails,
        loadLanguages,
        loadModels,
        removeFault,
        clearTroubleshootingMessages,
        clearFaultDetails,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Troubleshooting)

